import React, { useMemo } from 'react';
import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import { useCollectionBinding, useSlotGroup, useGallery } from '@vibesites/runtime/react';
import { EditableSlotGroup, RichTextContent } from '@vibesites/runtime/react';
import type { PhotoGallery } from '@vibesites/schema';
import { useScrollReveal, useStaggerReveal } from '../hooks/useScrollReveal';
import { useRevealStyles } from '../hooks/useRevealStyles';
import { useCtaSheenStyles } from '../hooks/useCtaSheenStyles';
import { GeometricPatternBackground } from '../utils/geometricPattern';
import { VibesitesImage } from '@vibesites/ui-primitives';

interface Floorplan {
  floorplan_id?: string | null;
  floorplan_name?: string | null;
  floorplan_image_url?: string | null;
  floorplan_beds_min?: string | null;
  floorplan_beds_max?: string | null;
  floorplan_baths_min?: string | null;
  floorplan_sqft_min?: string | null;
  floorplan_sqft_max?: string | null;
  floorplan_price_min?: string | null;
  floorplan_price_max?: string | null;
  floorplan_available_units?: string | null;
}

// ============================================================================
// Bedroom category helpers
// ============================================================================

interface BedroomCategory {
  key: string;
  label: string;
  subtitle: string;
  param: string;
  count: number;
  priceRange: string;
}

const BED_ORDER = ['studio', 'one', 'two', 'three', 'four', 'five'];

function bedCountToKey(bedsRaw: string | null | undefined): string {
  if (!bedsRaw) return 'studio';
  const trimmed = bedsRaw.trim().toLowerCase();
  if (trimmed === '0' || trimmed === 'studio' || trimmed === 'std') return 'studio';
  const num = parseInt(trimmed, 10);
  if (isNaN(num) || num <= 0) return 'studio';
  const names = ['studio', 'one', 'two', 'three', 'four', 'five'];
  return names[num] || `${num}`;
}

function keyToLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bedroom',
    two: '2 Bedroom',
    three: '3 Bedroom',
    four: '4 Bedroom',
    five: '5 Bedroom',
  };
  return labels[key] || `${key} Bedroom`;
}

function keyToShortLabel(key: string): string {
  const labels: Record<string, string> = {
    studio: 'Studio',
    one: '1 Bed',
    two: '2 Bed',
    three: '3 Bed',
    four: '4 Bed',
    five: '5 Bed',
  };
  return labels[key] || `${key} Bed`;
}

function formatPriceRange(plans: Floorplan[]): string {
  const prices = plans
    .flatMap((p) => [p.floorplan_price_min, p.floorplan_price_max])
    .map((v) => (v ? parseInt(String(v), 10) : NaN))
    .filter((v) => !isNaN(v) && v > 0);
  if (!prices.length) return 'Call for Pricing';
  const min = Math.min(...prices);
  const max = Math.max(...prices);
  if (min === max) return `From $${min.toLocaleString()}`;
  return `$${min.toLocaleString()} - $${max.toLocaleString()}`;
}

function groupFloorplansByBeds(floorplans: Floorplan[]): BedroomCategory[] {
  const groups: Record<string, Floorplan[]> = {};

  for (const plan of floorplans) {
    const key = bedCountToKey(plan.floorplan_beds_min);
    if (!groups[key]) groups[key] = [];
    groups[key].push(plan);
  }

  const sorted = Object.keys(groups).sort((a, b) => {
    const ai = BED_ORDER.indexOf(a);
    const bi = BED_ORDER.indexOf(b);
    return (ai === -1 ? 99 : ai) - (bi === -1 ? 99 : bi);
  });

  return sorted.map((key) => {
    const plans = groups[key];
    const count = plans.reduce((sum, p) => {
      const units = p.floorplan_available_units ? parseInt(String(p.floorplan_available_units), 10) : 0;
      return sum + (isNaN(units) ? 0 : units);
    }, 0);
    return {
      key,
      label: keyToLabel(key),
      subtitle: keyToShortLabel(key),
      param: key,
      count,
      priceRange: formatPriceRange(plans),
    };
  });
}

// ============================================================================
// Interior photo selection from gallery
// ============================================================================

const INTERIOR_KEYWORDS = [
  'interior', 'apartment', 'unit', 'bedroom', 'living room', 'living',
  'kitchen', 'bathroom', 'bath', 'inside', 'home', 'den', 'dining',
  'closet', 'counter', 'cabinet', 'window', 'floor', 'carpet',
  'hardwood', 'granite', 'stainless', 'appliance', 'washer', 'dryer',
];

function scoreInterior(photo: PhotoGallery): number {
  const text = [photo.title, photo.description, photo.alt_text]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!text) return 0;
  return INTERIOR_KEYWORDS.reduce((s, kw) => s + (text.includes(kw) ? 1 : 0), 0);
}

/**
 * Strips query params and trailing slashes so CDN variants of
 * the same image (e.g. ?width=400 vs ?width=800) are treated as one.
 */
function normalizeUrl(url: string): string {
  try {
    const u = new URL(url);
    return (u.origin + u.pathname).replace(/\/+$/, '').toLowerCase();
  } catch {
    return url.toLowerCase().split('?')[0].replace(/\/+$/, '');
  }
}

/**
 * Picks the best interior photo gallery indices for the floorplan cards.
 * Returns indices into the original gallery array so they can be used
 * as `defaultIndex` in slot group sourceBindings.
 */
function pickInteriorPhotoIndices(gallery: PhotoGallery[], count: number): number[] {
  if (!gallery.length) return Array.from({ length: count }, (_, i) => i);

  // Score every gallery item for "interior" relevance, deduplicate by URL
  const seen = new Set<string>();
  const scored: { originalIndex: number; score: number }[] = [];
  for (let i = 0; i < gallery.length; i++) {
    const photo = gallery[i];
    if (!photo.image) continue;
    const key = normalizeUrl(photo.image);
    if (seen.has(key)) continue;
    seen.add(key);
    scored.push({ originalIndex: i, score: scoreInterior(photo) });
  }

  // Sort by score descending
  scored.sort((a, b) => b.score - a.score);

  // Take the top-scored unique photos, then spread them out to avoid
  // visually similar adjacent photos (e.g. 3 kitchen shots in a row).
  const pool = scored.slice(0, Math.max(count * 2, 8));
  if (pool.length <= count) {
    const indices = pool.map((p) => p.originalIndex);
    // Pad with sequential indices if gallery is small
    while (indices.length < count) {
      indices.push(indices.length < gallery.length ? indices.length : 0);
    }
    return indices;
  }

  // Pick every Nth item to space them out visually
  const step = pool.length / count;
  const picked: number[] = [];
  for (let i = 0; i < count; i++) {
    const idx = Math.min(Math.floor(i * step), pool.length - 1);
    picked.push(pool[idx].originalIndex);
  }
  return picked;
}

/**
 * Hook that selects the 5 best interior photo gallery indices for the
 * floorplan card images. These are used as smart defaults for the
 * overridable image slot groups.
 */
function useSmartInteriorIndices(): [number, number, number, number, number] {
  const gallery = useGallery();
  return useMemo(() => {
    const indices = pickInteriorPhotoIndices(gallery, 5);
    return indices as [number, number, number, number, number];
  }, [gallery]);
}

// ============================================================================
// Slot group helpers for overridable card images
// ============================================================================

/** Creates a dynamic slot group definition for a card image at a given gallery index. */
function makeCardImageSlotDef(label: string, index: number): SlotGroupDefinition {
  return {
    label,
    fields: {
      image: { label: 'Image', type: 'image', defaultField: `photo_gallery[${index}].image` },
      alt: { label: 'Alt Text', type: 'text', defaultField: `photo_gallery[${index}].alt_text` },
    },
    sourceBinding: {
      collection: 'photo_gallery',
      defaultIndex: index,
      fieldMappings: { image: 'image', alt: 'alt_text' },
    },
  };
}

// Static slot group definitions for contentSchema (must use fixed default indices)
const cardImage0Def: SlotGroupDefinition = {
  label: 'View All Card Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[0].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[0].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 0, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const cardImage1Def: SlotGroupDefinition = {
  label: 'Card 2 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[1].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[1].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 1, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const cardImage2Def: SlotGroupDefinition = {
  label: 'Card 3 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[2].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[2].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 2, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const cardImage3Def: SlotGroupDefinition = {
  label: 'Card 4 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[3].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[3].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 3, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const cardImage4Def: SlotGroupDefinition = {
  label: 'Card 5 Image',
  fields: {
    image: { label: 'Image', type: 'image', defaultField: 'photo_gallery[4].image' },
    alt: { label: 'Alt Text', type: 'text', defaultField: 'photo_gallery[4].alt_text' },
  },
  sourceBinding: { collection: 'photo_gallery', defaultIndex: 4, fieldMappings: { image: 'image', alt: 'alt_text' } },
};

const ctaLinkDef: SlotGroupDefinition = {
  label: 'Floor Plans CTA',
  fields: {
    url: { label: 'URL', type: 'url', defaultValue: '/floor-plans' },
    label: { label: 'Label', type: 'text', defaultValue: 'View Floor Plans' },
    _target: { label: 'Link Target', type: 'text', defaultValue: '_self' },
  },
};

// ============================================================================
// Category card sub-component (renders card image + overlay + label)
// ============================================================================

interface CategoryCardProps {
  cat: BedroomCategory;
  children: React.ReactNode; // The <EditableSlotGroup> wrapper with literal groupPath
}

function CategoryCard({ cat, children }: CategoryCardProps) {
  return (
    <a
      data-reveal
      href={`/floor-plans?bedroom=${cat.param}`}
      className="reveal-fade-up fp-card group relative block aspect-square overflow-hidden cursor-pointer fp-card-item"
      style={{ flex: `1 1 0%`, minWidth: 0 } as React.CSSProperties}
    >
      {children}
      <div className="fp-card-overlay-layer absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
      <div className="fp-card-label-layer relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4">
        <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">
          {cat.key === 'studio' ? 'S' : BED_ORDER.indexOf(cat.key).toString()}
        </span>
        <span className="font-body font-bold text-[11px] sm:text-xs md:text-sm uppercase tracking-[0.2em] drop-shadow-md">
          {cat.label} {cat.key === 'studio' ? '' : 'Layouts'}
        </span>
      </div>
    </a>
  );
}

function CardImageContent({ img, fallbackAlt }: { img: { image?: string; alt?: string }; fallbackAlt: string }) {
  return img.image ? (
    <VibesitesImage
      url={img.image}
      alt={img.alt || fallbackAlt}
      className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
    />
  ) : (
    <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
  );
}

// ============================================================================
// Component
// ============================================================================

export function FloorplansList() {
  const floorplans = useCollectionBinding<Floorplan>('floorplanslist.items', {
    collection: 'floorplans',
  });

  const categories = useMemo(
    () => (floorplans?.length ? groupFloorplansByBeds(floorplans) : []),
    [floorplans],
  );

  // Smart interior photo selection — picks the best gallery indices as defaults.
  // These are used as dynamic defaultIndex values so the slot groups get
  // intelligent defaults while remaining fully overridable by the user.
  const [idx0, idx1, idx2, idx3, idx4] = useSmartInteriorIndices();

  // Runtime slot defs with smart photo indices (may differ from static schema defaults)
  const def0 = useMemo(() => makeCardImageSlotDef('View All Card Image', idx0), [idx0]);
  const def1 = useMemo(() => makeCardImageSlotDef('Card 2 Image', idx1), [idx1]);
  const def2 = useMemo(() => makeCardImageSlotDef('Card 3 Image', idx2), [idx2]);
  const def3 = useMemo(() => makeCardImageSlotDef('Card 4 Image', idx3), [idx3]);
  const def4 = useMemo(() => makeCardImageSlotDef('Card 5 Image', idx4), [idx4]);

  // Overridable image slots — users can select/replace these via the editor
  const img0 = useSlotGroup('floorplanslist.card_image_0', def0);
  const img1 = useSlotGroup('floorplanslist.card_image_1', def1);
  const img2 = useSlotGroup('floorplanslist.card_image_2', def2);
  const img3 = useSlotGroup('floorplanslist.card_image_3', def3);
  const img4 = useSlotGroup('floorplanslist.card_image_4', def4);
  const ctaLink = useSlotGroup('floorplanslist.cta_link', ctaLinkDef);

  // Category cards with their image slots (indices 1-4)
  const catSlotImages = [img1, img2, img3, img4];
  const catSlotDefs = [def1, def2, def3, def4];

  useRevealStyles();
  useCtaSheenStyles();
  const headerRef = useScrollReveal<HTMLDivElement>();
  const gridRef = useStaggerReveal<HTMLDivElement>({ staggerMs: 120 });

  const planCount = floorplans?.length ?? 0;
  // Treat a single bedroom category as one "option" — the browse-by-beds
  // card grid is redundant when every plan shares the same bed count.
  const isSingleOption = categories.length <= 1;
  // Mobile uses a 2-column wrap; when categories + the "ALL" CTA leave an
  // odd total, pair layout shows empty space — stack full-width instead.
  const stackOnMobile = (categories.length + 1) % 2 !== 0;

  // Nothing to promote when the property has no floor plans at all.
  if (planCount === 0) {
    return null;
  }

  return (
    <section className="relative overflow-hidden pt-10 pb-4 md:pt-14 md:pb-6 bg-[var(--vibe-color-background)]">
      <GeometricPatternBackground />

      <div className="relative z-10 max-w-[1400px] mx-auto px-2 sm:px-3 lg:px-4">
        <div ref={headerRef} className="reveal-fade-up text-center mb-8 md:mb-10 px-2 sm:px-4">
          <RichTextContent
            path="floorplanslist.title"
            label="Title"
            as="h2"
            className="font-heading text-3xl sm:text-4xl md:text-5xl font-normal text-[var(--vibe-color-text)] mb-4 leading-tight"
          />
          {isSingleOption ? (
            <RichTextContent
              path="floorplanslist.single_subtitle"
              label="Subtitle (Single Floor Plan)"
              as="p"
              className="font-body text-sm sm:text-base text-[var(--vibe-color-text)] max-w-4xl mx-auto leading-relaxed [&_a]:text-[var(--vibe-color-text)] [&_a]:underline [&_a]:decoration-[var(--vibe-color-primary)] [&_a]:underline-offset-2 [&_a:hover]:decoration-2"
            />
          ) : (
            <RichTextContent
              path="floorplanslist.subtitle"
              label="Subtitle"
              as="p"
              className="font-body text-sm sm:text-base text-[var(--vibe-color-text)] max-w-4xl mx-auto leading-relaxed [&_a]:text-[var(--vibe-color-text)] [&_a]:underline [&_a]:decoration-[var(--vibe-color-primary)] [&_a]:underline-offset-2 [&_a:hover]:decoration-2"
            />
          )}
        </div>

        {isSingleOption ? (
          <div className="reveal-fade-up flex justify-center px-2 sm:px-4 pb-2">
            <EditableSlotGroup groupPath="floorplanslist.cta_link" definition={ctaLinkDef}>
              <a
                href={ctaLink.url || '/floor-plans'}
                target={ctaLink._target || '_self'}
                className="fp-single-cta vantage-cta-sheen"
              >
                <span className="relative z-10">{ctaLink.label || 'View Floor Plans'}</span>
              </a>
            </EditableSlotGroup>
          </div>
        ) : (
          <div
            ref={gridRef}
            className={`fp-grid flex flex-wrap md:flex-nowrap gap-3 md:gap-4${stackOnMobile ? ' fp-grid--stack' : ''}`}
          >
            {/* "View All" card first — uses slot index 0 */}
            <a
              data-reveal
              href="/floor-plans"
              className="reveal-fade-up fp-card group relative block aspect-square overflow-hidden cursor-pointer fp-card-item"
              style={{ flex: `1 1 0%`, minWidth: 0 } as React.CSSProperties}
            >
              <EditableSlotGroup groupPath="floorplanslist.card_image_0" definition={def0} className="absolute inset-0">
                {img0.image ? (
                  <VibesitesImage
                    url={img0.image}
                    alt={img0.alt || 'View all floor plans'}
                    className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-700 ease-out group-hover:scale-110"
                  />
                ) : (
                  <div className="absolute inset-0 bg-[var(--vibe-color-surface)]" />
                )}
              </EditableSlotGroup>
              <div className="fp-card-overlay-layer absolute inset-0 fp-card-overlay transition-opacity duration-300 group-hover:opacity-90" />
              <div className="fp-card-label-layer relative z-10 flex flex-col items-center justify-center h-full text-center text-white px-4">
                <span className="font-heading text-5xl sm:text-6xl md:text-7xl font-bold leading-none mb-3 drop-shadow-lg">ALL</span>
                <span className="font-body font-bold text-[11px] sm:text-xs md:text-sm uppercase tracking-[0.2em] drop-shadow-md">View All Layouts</span>
              </div>
            </a>

            {/* Bedroom category cards — each uses a literal groupPath for static linter detection */}
            {categories.length > 0 && (
              <CategoryCard cat={categories[0]}>
                <EditableSlotGroup groupPath="floorplanslist.card_image_1" definition={catSlotDefs[0]} className="absolute inset-0">
                  <CardImageContent img={catSlotImages[0]} fallbackAlt={`${categories[0].label} floor plans`} />
                </EditableSlotGroup>
              </CategoryCard>
            )}
            {categories.length > 1 && (
              <CategoryCard cat={categories[1]}>
                <EditableSlotGroup groupPath="floorplanslist.card_image_2" definition={catSlotDefs[1]} className="absolute inset-0">
                  <CardImageContent img={catSlotImages[1]} fallbackAlt={`${categories[1].label} floor plans`} />
                </EditableSlotGroup>
              </CategoryCard>
            )}
            {categories.length > 2 && (
              <CategoryCard cat={categories[2]}>
                <EditableSlotGroup groupPath="floorplanslist.card_image_3" definition={catSlotDefs[2]} className="absolute inset-0">
                  <CardImageContent img={catSlotImages[2]} fallbackAlt={`${categories[2].label} floor plans`} />
                </EditableSlotGroup>
              </CategoryCard>
            )}
            {categories.length > 3 && (
              <CategoryCard cat={categories[3]}>
                <EditableSlotGroup groupPath="floorplanslist.card_image_4" definition={catSlotDefs[3]} className="absolute inset-0">
                  <CardImageContent img={catSlotImages[3]} fallbackAlt={`${categories[3].label} floor plans`} />
                </EditableSlotGroup>
              </CategoryCard>
            )}
          </div>
        )}
      </div>

      <style>{`
        .fp-single-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 0.95rem 1.75rem;
          font-family: var(--vibe-font-heading);
          font-size: 0.72rem;
          font-weight: 600;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }
        .fp-card {
          border-radius: 4px;
        }
        .fp-card-overlay {
          background: linear-gradient(
            180deg,
            rgba(0, 0, 0, 0.45) 0%,
            rgba(0, 0, 0, 0.65) 100%
          );
          opacity: 0.35;
        }
        .fp-card:hover .fp-card-overlay {
          opacity: 0.55;
        }
        /* In edit mode, the overlay tint and centered label sit on top of the
           image slot group. Let pointer events pass through them so the
           EditableSlotGroup beneath receives hover/click and each card tile
           can be individually highlighted and edited. The parent <a>'s
           :hover CSS still fires (it's based on cursor position), so the
           group-hover visual transitions continue to work. */
        [data-vibesite-edit-mode="true"] .fp-card-overlay-layer,
        [data-vibesite-edit-mode="true"] .fp-card-label-layer {
          pointer-events: none;
        }
        /* On mobile only, cards take ~half width so they wrap in pairs.
           Tablets (≥768px) use the single-row desktop layout.
           When categories + ALL CTA leave an odd count, stack full-width
           so the last row doesn't show empty space. */
        @media (max-width: 767px) {
          .fp-card-item {
            flex: 0 0 calc(50% - 0.375rem) !important;
            min-width: calc(50% - 0.375rem) !important;
          }
          .fp-grid--stack .fp-card-item {
            flex: 0 0 100% !important;
            min-width: 100% !important;
          }
        }
      `}</style>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'FloorplansList',
  displayName: 'Floor Plans List',
  tokens: {
    'floorplanslist.title': {
      label: 'Title',
      type: 'richtext',
      defaultValue: '<p>Make Yourself at Home</p>',
    },
    'floorplanslist.subtitle': {
      label: 'Subtitle',
      type: 'richtext',
      defaultValue:
        '<p>Please select your desired layout below or browse our <a href="/gallery">photo gallery</a>.</p>',
    },
    'floorplanslist.single_subtitle': {
      label: 'Subtitle (Single Floor Plan)',
      type: 'richtext',
      defaultValue:
        '<p>Explore our floor plan details, or browse our <a href="/gallery">photo gallery</a>.</p>',
    },
  },
  slotGroups: {
    'floorplanslist.card_image_0': cardImage0Def,
    'floorplanslist.card_image_1': cardImage1Def,
    'floorplanslist.card_image_2': cardImage2Def,
    'floorplanslist.card_image_3': cardImage3Def,
    'floorplanslist.card_image_4': cardImage4Def,
    'floorplanslist.cta_link': ctaLinkDef,
  },
  collectionBindings: {
    'floorplanslist.items': {
      label: 'Floor Plans',
      collection: 'floorplans',
    },
  },
};

(FloorplansList as any).contentSchema = contentSchema;

export default FloorplansList;
